import type { Difficulty, GameId, RoomMode } from "@playpal/core";
import { apiUrl } from "./config";

export interface Profile {
	code: string;
	name: string;
	avatar: string;
	globalOptIn: boolean;
	createdAt: number;
}

export interface GameStats {
	gameId: GameId | "all";
	mode: "friend" | "computer";
	played: number;
	won: number;
	lost: number;
	drawn: number;
	streak: number;
	bestStreak: number;
}

export interface OpponentRecord {
	code: string;
	name: string;
	avatar: string;
	played: number;
	won: number;
	lost: number;
	drawn: number;
	lastPlayedAt: number;
}

export interface LeaderboardRow {
	rank: number;
	/** Whether this row is you. The server decides; your code never leaves it. */
	you: boolean;
	name: string;
	avatar: string;
	played: number;
	won: number;
	winRate: number;
}

export class ApiError extends Error {
	constructor(
		readonly code: string,
		readonly status: number,
		message: string,
	) {
		super(message);
	}
}

/** Turns the server's error codes into something a 10-year-old can act on. */
const MESSAGES: Record<string, string> = {
	"unknown-player": "We couldn't find that player code. Check the letters and numbers.",
	"wrong-pin": "That PIN isn't right. Try again.",
	locked: "Too many tries. Wait five minutes and have another go.",
	"bad-pin": "A PIN is four numbers.",
	"bad-profile": "Pick a name and an animal from the lists.",
	"not-signed-in": "You've been signed out. Sign in again to see your record.",
	"room-create-failed": "Couldn't start a game. Try again in a moment.",
	"no-code-available": "Couldn't start a game. Try again in a moment.",
	// Deliberately not "you have been rate limited". A child who reaches this is
	// far more likely to be having a very good afternoon than attacking anything.
	"too-many": "That's a lot of games! Take a break and try again in a few minutes.",
};

async function request<T>(path: string, init: RequestInit = {}, token?: string | null): Promise<T> {
	const headers = new Headers(init.headers);
	if (init.body) headers.set("content-type", "application/json");
	if (token) headers.set("authorization", `Bearer ${token}`);

	let response: Response;
	try {
		response = await fetch(apiUrl(path), { ...init, headers });
	} catch {
		throw new ApiError("offline", 0, "No internet. Check the connection and try again.");
	}

	const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
	if (!response.ok) {
		const code = typeof body.error === "string" ? body.error : "unknown";
		throw new ApiError(code, response.status, MESSAGES[code] ?? "Something went wrong.");
	}
	return body as T;
}

export const api = {
	createRoom(options: { mode: RoomMode; difficulty?: Difficulty }) {
		return request<{ code: string }>("/api/rooms", {
			method: "POST",
			body: JSON.stringify(options),
		});
	},

	createProfile(input: { name: string; avatar: string; pin: string }) {
		return request<{ profile: Profile; token: string }>("/api/players", {
			method: "POST",
			body: JSON.stringify(input),
		});
	},

	signIn(input: { code: string; pin: string }) {
		return request<{ profile: Profile; token: string }>("/api/sessions", {
			method: "POST",
			body: JSON.stringify(input),
		});
	},

	signOut(token: string) {
		return request<{ ok: true }>("/api/sessions", { method: "DELETE" }, token);
	},

	/** Deletes the player card and everything recorded about it. Irreversible. */
	deleteMe(token: string) {
		return request<{ ok: true }>("/api/me", { method: "DELETE" }, token);
	},

	me(token: string) {
		return request<{ profile: Profile; stats: GameStats[]; opponents: OpponentRecord[] }>(
			"/api/me",
			{},
			token,
		);
	},

	updateMe(token: string, changes: { name?: string; avatar?: string; globalOptIn?: boolean }) {
		return request<{ profile: Profile }>(
			"/api/me",
			{ method: "PATCH", body: JSON.stringify(changes) },
			token,
		);
	},

	leaderboard(token?: string | null) {
		return request<{ rows: LeaderboardRow[] }>("/api/leaderboard", {}, token);
	},
};
